import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SiteNav, SiteFooter } from './HomePage';

const PROGRAMS = [
  { id: 'syep', title: 'Summer Youth Employment (SYEP)', icon: '☀️', color: '#E86C3D', path: '/programs/syep',
    features: ['Orientation workbooks (digital — no printing)', 'Worksite assignment tracking', 'Financial literacy workshops', 'Professional development courses', 'Weekly check-ins and reflections'],
    audience: 'City departments running summer jobs programs for ages 14-24' },
  { id: 'schools', title: 'School Districts', icon: '🏫', color: '#3B82F6', path: '/programs/schools',
    features: ['Student Pathways GPS career exploration', 'Counselor-student direct messaging', 'Quarterly progress reviews', 'College readiness workshops', 'Parent portal with progress visibility'],
    audience: 'School counselors and career readiness programs' },
  { id: 'nonprofit', title: 'Nonprofit Youth Programs', icon: '🤝', color: '#10B981', path: '/programs/nonprofit',
    features: ['Mentoring relationship management', 'Goal setting and tracking', 'Community project tracking', 'Leadership development workshops', 'Volunteer/staff coordination'],
    audience: 'Youth-serving nonprofits, community organizations, and foundations' },
  { id: 'workforce', title: 'Workforce Development', icon: '💼', color: '#8B5CF6', path: '/programs/workforce',
    features: ['Skills assessment and tracking', 'Job readiness training modules', 'Interview preparation workshops', 'Resume building with AI', 'Employer connection pipeline'],
    audience: 'Workforce boards, job training programs, and career centers' },
];

const ProgramsPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-white">
      <SiteNav />
      <div style={{ background: 'linear-gradient(135deg, #0A0E1A 0%, #2D2824 100%)' }} className="py-16 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-black text-white mb-3">Built for Every Youth Program</h1>
          <p className="text-white/60">One platform that adapts to your program type, your workflow, and your youth.</p>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 py-12 space-y-6">
        {PROGRAMS.map((p, i) => (
          <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
            className="rounded-2xl border border-gray-100 overflow-hidden hover:shadow-lg transition-all">
            <div className="h-1.5" style={{ backgroundColor: p.color }} />
            <div className="p-6">
              <div className="flex items-start gap-4 mb-4">
                <span className="text-4xl">{p.icon}</span>
                <div className="flex-1">
                  <h2 className="text-xl font-bold text-gray-900 mb-1">{p.title}</h2>
                  <p className="text-xs text-gray-500">{p.audience}</p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-2 mb-4">
                {p.features.map((f, fi) => (
                  <div key={fi} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: p.color }} />
                    <p className="text-sm text-gray-700">{f}</p>
                  </div>
                ))}
              </div>
              <Button onClick={() => navigate(p.path)} variant="outline" className="rounded-full text-sm font-bold" style={{ borderColor: p.color, color: p.color }}>
                Learn More <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </motion.div>
        ))}
      </div>
      <section className="py-16 px-4 bg-gray-50 text-center">
        <h2 className="text-2xl font-black text-gray-900 mb-3">Not sure which fits your program?</h2>
        <p className="text-gray-600 mb-6">We will help you figure it out. Every demo is customized to your program type.</p>
        <Button onClick={() => navigate('/demo')} className="rounded-full bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-5">
          Request a Demo <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </section>
      <SiteFooter />
    </div>
  );
};

export default ProgramsPage;
